/**
 * KeyEngraving — the key they just earned, standing in the dark, blank,
 * with one button: Engrave it. It opens a small modal for their first name
 * (filled in when we already have it); on submit the modal goes, and they
 * watch a laser burn the name along the shaft, letter by letter (each one
 * glowing hot, then cooling into the metal, sparks off the beam); the key
 * turns once, and onDone runs. Shown over the debrief once the report is filed.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from "react";
import { toast } from "sonner";
import { ACTION_BTN_CLASS } from "./step-shell";
import keyImg from "./key-engrave.webp";
import {
  NAME_MAX,
  NAME_RE,
  cleanName,
  markEngraved,
  saveName,
  useMemberName,
} from "./operative-name";

/** key-engrave.webp: width / height, and the shaft the name runs along, as
 *  fractions of the image (measured off the art). */
const KEY_ASPECT = 322 / 1000;
const SHAFT = { top: 0.4, bottom: 0.8, centerX: 0.5 };
/** How long the laser spends on each letter. */
const LETTER_MS = 260;
/** How long the key takes to seal once the name is set: it tilts to catch
 *  the light (never edge-on — it is a flat picture) while a glint crosses it. */
const TURN_MS = 1400;

export function KeyEngraving({
  eyebrow,
  title,
  prompt,
  buttonLabel,
  onDone,
}: {
  eyebrow: string;
  title: string;
  prompt: string;
  buttonLabel: string;
  onDone: () => void;
}) {
  // known from the Name step; asked for only when this device never saw it
  // the name on their member, from the server (never just this browser: a
  // texted link on a new device still knows who they are)
  const { data: known = "", isLoading: nameLoading } = useMemberName();
  const [typed, setTyped] = useState<string | null>(null);
  const name = typed ?? known;
  const setName = setTyped;
  const [etched, setEtched] = useState(0);
  const [lasering, setLasering] = useState(false);
  const [turning, setTurning] = useState(false);
  // the name modal, open from Engrave it until they submit
  const [asking, setAsking] = useState(false);
  const trimmed = name.trim();
  const valid = trimmed.length > 0 && NAME_RE.test(trimmed);
  const letters = [...trimmed.toUpperCase()];

  const engrave = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || lasering || turning) return;
    // the keyboard down and the modal gone: they watch the key
    (document.activeElement as HTMLElement | null)?.blur();
    setAsking(false);
    requestAnimationFrame(() => window.scrollTo(0, 0));
    // typed here: save it (the texts address them by it). A failed save must
    // not strand them on the key.
    if (trimmed !== known)
      void saveName(trimmed).catch(() =>
        toast.error("Couldn't save your name — carrying on without it."),
      );
    setEtched(0);
    setLasering(true);
  };

  // Escape closes the name modal, as × does
  useEffect(() => {
    if (!asking) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setAsking(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [asking]);

  // the key turns, then the debrief goes on (not once it has gone)
  useEffect(() => {
    if (!turning) return;
    const t = setTimeout(onDone, TURN_MS);
    return () => clearTimeout(t);
    // onDone is the debrief's dispatch: the run is started once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turning]);

  // the laser moves on a letter at a time; then the key turns
  useEffect(() => {
    if (!lasering) return;
    if (etched >= letters.length) {
      const t = setTimeout(() => {
        setLasering(false);
        setTurning(true);
        markEngraved(trimmed);
      }, 450);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setEtched((n) => n + 1), LETTER_MS);
    return () => clearTimeout(t);
    // onDone is the debrief's dispatch: the run is started once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lasering, etched, letters.length]);

  // where the beam meets the metal: the letter being burned, in the key's box
  const box = useRef<HTMLDivElement>(null);
  const spans = useRef<(HTMLSpanElement | null)[]>([]);
  const [spot, setSpot] = useState<{ x: number; y: number } | null>(null);
  useLayoutEffect(() => {
    const el = spans.current[Math.min(etched, letters.length - 1)];
    const b = box.current?.getBoundingClientRect();
    if (!lasering || !el || !b) {
      setSpot(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setSpot({
      x: r.left + r.width / 2 - b.left,
      y: r.top + r.height / 2 - b.top,
    });
  }, [lasering, etched, letters.length]);

  // Etched along the shaft, reading upward like a stamp on a key blade; sized
  // to the shaft's width, and shrunk further when a long name needs the room.
  const shaftLen = SHAFT.bottom - SHAFT.top;
  const fontCqh = Math.min(
    3.9,
    (shaftLen * 100 * 0.92) / Math.max(1, letters.length * 0.72),
  );

  return (
    <div className="absolute inset-0 z-[21] flex animate-[smFade_.6s_ease_both] flex-col items-center overflow-clip bg-black px-6 pt-[max(22px,4dvh)] pb-[max(24px,4dvh)] text-center font-serif">
      <div className="font-stencil text-[11px] tracking-[4px] text-[#d6a94e] uppercase [text-shadow:0_0_14px_rgba(214,169,78,.5)]">
        ▸ {eyebrow} ◂
      </div>
      <div className="mt-2 max-w-[360px] font-stencil text-[clamp(17px,5.4vw,23px)] leading-[1.2] tracking-[1px] text-balance text-[#f4eddb] uppercase">
        {title}
      </div>

      {/* the key, sized by height; the name is placed in its own units (cqh) */}
      <div className="relative my-[2dvh] min-h-0 w-full flex-1 [perspective:900px]">
        <div
          ref={box}
          style={{ aspectRatio: KEY_ASPECT }}
          className={`absolute top-0 left-1/2 h-full -translate-x-1/2 [container-type:size] ${
            turning
              ? "animate-[smKeyTilt_1.4s_ease-in-out_both]"
              : "animate-[smKeyIn_.8s_cubic-bezier(.2,.8,.3,1)_both]"
          }`}
        >
          <img
            src={keyImg}
            alt=""
            className="block h-full w-full drop-shadow-[0_18px_40px_rgba(214,169,78,.18)]"
          />
          {/* the glint: a band of light across the metal, masked to the key */}
          {turning ? (
            <div
              aria-hidden
              style={{
                maskImage: `url(${keyImg})`,
                WebkitMaskImage: `url(${keyImg})`,
                maskSize: "100% 100%",
                WebkitMaskSize: "100% 100%",
              }}
              className="pointer-events-none absolute inset-0 animate-[smGlint_1.1s_ease-in-out_.15s_both] bg-[linear-gradient(115deg,transparent_35%,rgba(255,236,190,.85)_50%,transparent_65%)] bg-[length:250%_100%] mix-blend-screen"
            />
          ) : null}
          <div
            aria-hidden
            style={{
              left: `${SHAFT.centerX * 100}%`,
              top: `${((SHAFT.top + SHAFT.bottom) / 2) * 100}%`,
              width: `${shaftLen * 100}cqh`,
              fontSize: `${fontCqh}cqh`,
            }}
            className={`pointer-events-none absolute flex -translate-x-1/2 -translate-y-1/2 -rotate-90 items-center justify-center font-stencil leading-none tracking-[.14em] whitespace-nowrap ${turning ? "animate-[smSeal_1s_ease-out_.3s_both]" : ""}`}
          >
            {letters.map((ch, i) => (
              <span
                key={i}
                ref={(el) => {
                  spans.current[i] = el;
                }}
                // burned in as the laser reaches it; blank until then
                className={
                  i < etched || turning
                    ? "animate-[smEtch_.9s_ease-out_both] text-[#2b1a0b] [text-shadow:0_1px_0_rgba(255,214,150,.45),0_-1px_0_rgba(0,0,0,.6)]"
                    : "opacity-0"
                }
              >
                {ch === " " ? " " : ch}
              </span>
            ))}
          </div>

          {/* the laser: a beam in out of the dark, from up and to the right, onto the letter being
              burned, a white-hot spot where it lands, sparks thrown off */}
          {spot ? (
            <div
              aria-hidden
              style={{ left: spot.x, top: spot.y }}
              className="pointer-events-none absolute z-[2] size-0 transition-[left,top] duration-200 ease-out"
            >
              <div className="absolute bottom-0 left-1/2 h-[120cqh] w-[3px] origin-bottom -translate-x-1/2 rotate-[38deg] animate-[smBeam_.12s_steps(2)_infinite] bg-[linear-gradient(180deg,transparent,rgba(255,60,40,.35)_40%,#ff3b2e_85%,#fff_100%)] shadow-[0_0_10px_3px_rgba(255,59,46,.55)]" />
              <div className="absolute size-[14px] -translate-1/2 rounded-full bg-white shadow-[0_0_8px_4px_#ffd2a0,0_0_22px_10px_rgba(255,90,40,.8)]" />
              {[-60, -25, 10, 40, 75, 115, 150, 200].map((deg, i) => (
                <span
                  key={`${etched}-${deg}`}
                  style={
                    {
                      "--sx": `${Math.cos((deg * Math.PI) / 180) * (18 + (i % 3) * 8)}px`,
                      "--sy": `${Math.sin((deg * Math.PI) / 180) * (18 + (i % 3) * 8)}px`,
                      animationDelay: `${(i % 4) * 30}ms`,
                    } as CSSProperties
                  }
                  className="absolute size-[3px] -translate-1/2 animate-[smSpark_.38s_ease-out_both] rounded-full bg-[#ffd27a] shadow-[0_0_4px_#ff8a2a]"
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="w-full max-w-[340px]">
        <p className="text-[16px] leading-[1.35] text-[#cfc3a8] italic">
          {prompt}
        </p>
        <button
          type="button"
          disabled={nameLoading || lasering || turning}
          onClick={() => setAsking(true)}
          className={`mt-3 w-full ${ACTION_BTN_CLASS} rounded-[9px] px-6 py-3 text-[17px] font-bold disabled:opacity-50`}
        >
          {lasering ? "Engraving…" : buttonLabel}
        </button>
      </div>

      {/* their name, in a modal over the key (high up, clear of the
          keyboard); a tap outside or the x closes it */}
      {asking ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="engrave-title"
          onClick={() => setAsking(false)}
          className="absolute inset-0 z-[5] flex animate-[smFade_.2s_ease_both] items-start justify-center bg-black/70 px-4 pt-[16dvh] font-sans"
        >
          <form
            onSubmit={engrave}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[360px] animate-[smModalIn_.3s_cubic-bezier(.2,.8,.3,1)_both] rounded-xl border border-[#d6a94e]/45 bg-[rgb(18_16_18/0.97)] px-[22px] pt-[20px] pb-[18px] text-left shadow-[0_20px_60px_rgba(0,0,0,.55)]"
          >
            <div className="mb-[14px] flex items-center justify-between">
              <h2
                id="engrave-title"
                className="m-0 text-lg font-bold text-[#f4eddb]"
              >
                Engrave your key
              </h2>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setAsking(false)}
                className="cursor-pointer border-none bg-transparent p-1 text-[22px] leading-none text-[#a99d86]"
              >
                ×
              </button>
            </div>
            <label
              htmlFor="engrave-name"
              className="mb-2 block text-xs tracking-[1px] text-[#a99d86] uppercase"
            >
              Your first name
            </label>
            <input
              id="engrave-name"
              autoFocus
              autoComplete="given-name"
              autoCapitalize="words"
              maxLength={NAME_MAX}
              placeholder="First name"
              value={name}
              onChange={(e) => setName(cleanName(e.target.value))}
              className="w-full rounded-lg border border-[#d6a94e]/45 bg-black/35 px-4 py-3 text-center font-stencil text-[18px] tracking-[.12em] text-[#f4eddb] uppercase outline-none placeholder:font-sans placeholder:text-[16px] placeholder:tracking-normal placeholder:normal-case placeholder:text-[#a99d86]/60 focus:border-[#d6a94e]"
            />
            <button
              type="submit"
              disabled={!valid}
              className={`mt-[14px] w-full ${ACTION_BTN_CLASS} rounded-lg px-4 py-3 text-[15px] font-semibold disabled:opacity-50`}
            >
              {buttonLabel}
            </button>
          </form>
        </div>
      ) : null}

      <style>{`
        @keyframes smFade{from{opacity:0}to{opacity:1}}
        @keyframes smModalIn{from{opacity:0;transform:translateY(12px) scale(.97)}to{opacity:1;transform:none}}
        @keyframes smKeyIn{from{opacity:0;transform:translateY(6%) scale(.96)}to{opacity:1;transform:none}}
        @keyframes smKeyTilt{0%{transform:rotateY(0)}30%{transform:rotateY(-18deg)}65%{transform:rotateY(14deg)}100%{transform:rotateY(0)}}
        @keyframes smSeal{0%{filter:none}35%{filter:drop-shadow(0 0 3px #ffb347) drop-shadow(0 0 8px #ff8a2a) brightness(1.8)}100%{filter:none}}
        @keyframes smGlint{from{background-position:100% 0}to{background-position:-60% 0}}
        @keyframes smEtch{0%{color:#fff8e0;text-shadow:0 0 8px #ffd27a,0 0 18px #ff5a1a}55%{color:#d0702a;text-shadow:0 0 5px rgba(255,120,40,.7)}100%{color:#2b1a0b}}
        @keyframes smBeam{from{opacity:1}to{opacity:.7}}
        @keyframes smSpark{0%{opacity:1;translate:0 0}100%{opacity:0;translate:var(--sx) var(--sy)}}
      `}</style>
    </div>
  );
}
